import { db } from '../firebase/config';
import {
  collection, addDoc, getDocs,
  query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';

const COL = 'withdrawal_requests';

export const WITHDRAWAL_MIN_AMOUNT = 10000;
export const WITHHOLDING_RATE_PERSONAL = 0.033; // 원천징수 3.3%
export const VAT_RATE_BUSINESS = 0.10;          // 부가세 10%

/**
 * 출금 세금 계산
 * @param {number} amount 출금 신청 금액
 * @param {'personal'|'business'} accountType
 * @returns {{taxType, taxRate, taxAmount, netAmount}}
 */
export const calcWithdrawalTax = (amount, accountType) => {
  const a = Math.max(0, Math.floor(Number(amount) || 0));
  if (accountType === 'business') {
    const taxAmount = Math.floor(a * VAT_RATE_BUSINESS);
    return { taxType: 'vat', taxRate: VAT_RATE_BUSINESS, taxAmount, netAmount: a - taxAmount };
  }
  // 기본 개인 원천징수
  const taxAmount = Math.floor(a * WITHHOLDING_RATE_PERSONAL);
  return { taxType: 'withholding', taxRate: WITHHOLDING_RATE_PERSONAL, taxAmount, netAmount: a - taxAmount };
};

/**
 * 출금 신청 생성 — 세금 자동 계산 후 저장
 */
export const createWithdrawal = async (realtorId, data) => {
  const { taxType, taxRate, taxAmount, netAmount } = calcWithdrawalTax(data.amount, data.accountType);
  const ref = await addDoc(collection(db, COL), {
    realtorId,
    amount: data.amount,
    bankName: data.bankName,
    accountNumber: data.accountNumber,
    accountHolder: data.accountHolder,
    accountType: data.accountType || 'personal',
    taxType,
    taxRate,
    taxAmount,
    netAmount,
    status: 'requested',
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

/**
 * 부동산별 출금 내역 조회 (연/월 필터)
 */
export const getWithdrawalsByRealtor = async (realtorId, year, month) => {
  // realtorId (camelCase) 또는 realtor_id (snake_case) 둘 다 조회
  const [snapA, snapB] = await Promise.all([
    getDocs(query(collection(db, COL), where('realtorId', '==', realtorId))).catch(() => ({ docs: [] })),
    getDocs(query(collection(db, COL), where('realtor_id', '==', realtorId))).catch(() => ({ docs: [] })),
  ]);
  const seen = new Set();
  const merged = [];
  for (const d of [...snapA.docs, ...snapB.docs]) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    merged.push(d);
  }

  const all = merged.map((d) => {
    const data = d.data();
    const rawTs = data.createdAt || data.created_at;
    let date = null;
    if (rawTs?.toDate) date = rawTs.toDate();
    else if (rawTs?.seconds) date = new Date(rawTs.seconds * 1000);
    return {
      id: d.id,
      ...data,
      createdAt: date?.toISOString().slice(0, 10) || '',
      _year: date?.getFullYear(),
      _month: date ? date.getMonth() + 1 : null,
      _ts: date ? date.getTime() : 0,
    };
  }).sort((a, b) => b._ts - a._ts);

  if (year && month) {
    return all.filter((r) => r._year === Number(year) && r._month === Number(month));
  }
  return all;
};

/**
 * 출금 요약 (총 출금액, 대기 건수)
 */
export const getWithdrawalSummary = async (realtorId) => {
  const q = query(
    collection(db, COL),
    where('realtorId', '==', realtorId)
  );
  const snap = await getDocs(q);

  let totalWithdrawn = 0;
  let pendingCount = 0;

  snap.docs.forEach((d) => {
    const data = d.data();
    if (data.status === 'completed') {
      totalWithdrawn += data.amount || 0;
    }
    if (data.status === 'requested') {
      pendingCount += 1;
    }
  });

  return { totalWithdrawn, pendingCount };
};
